import React, { useState, useEffect, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import axios from "axios";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerDeliverySetup from "@/pages/CustomerDeliverySetup";
import POS from "@/pages/POS";
import Inventory from "@/pages/Inventory";
import Quotations from "@/pages/Quotations";
import Invoices from "@/pages/Invoices";
import CreditInvoices from "@/pages/CreditInvoices";
import CustomerInvoices from "@/pages/CustomerInvoices";
import UserManagement from "@/pages/UserManagement";
import Customers from "@/pages/Customers";
import WebsiteCMS from "@/pages/WebsiteCMS";
import PublicWebsite from "@/pages/PublicWebsite";
import Settings from "@/pages/Settings";
import InvoiceQuotationSettings from "@/pages/InvoiceQuotationSettings";
import CurrencySettings from "@/pages/CurrencySettings";
import AccountingDashboard from "@/pages/AccountingDashboard";
import Expenses from "@/pages/Expenses";
import SettledExpenses from "@/pages/SettledExpenses";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Suppliers from "@/pages/Suppliers";
import Budget from "@/pages/Budget";
import SalesReports from "@/pages/SalesReports";
import SalesHistory from "@/pages/SalesHistory";
import ProductDetail from "@/pages/ProductDetail";
import WriteReview from "@/pages/WriteReview";
import Costing from "@/pages/Costing";
import Orders from "@/pages/Orders";
import Tax from "@/pages/Tax";
import PettyCash from "@/pages/PettyCash";
import Loans from "@/pages/Loans";
import Transfers from "@/pages/Transfers";
import Profile from "@/pages/Profile";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState({
    page_title: 'ASSETCO - Your Business Partner',
    favicon: null
  });

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // Fetch site settings (favicon and page title)
    fetchSiteSettings();
  }, [token]);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/website/content/settings`);
      const settings = response.data.content || {};
      setSiteSettings({
        page_title: settings.page_title || 'ASSETCO - Your Business Partner',
        favicon: settings.favicon || null
      });
      
      // Apply favicon dynamically
      if (settings.favicon) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = settings.favicon;
        if (!document.querySelector("link[rel~='icon']")) {
          document.head.appendChild(link);
        }
      }
      
      // Apply page title
      if (settings.page_title) {
        document.title = settings.page_title;
      }
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Auto-logout on inactivity
  useEffect(() => {
    if (!token) return; // Only run if user is logged in

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
    let inactivityTimer;

    const resetTimer = () => {
      // Clear existing timer
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      // Set new timer
      inactivityTimer = setTimeout(() => {
        console.log('User inactive for 30 minutes. Logging out...');
        logout();
        window.location.href = '/login';
      }, INACTIVITY_TIMEOUT);
    };

    // Events that indicate user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <AuthContext.Provider value={{ user, setUser, token, login, logout }}>
        <div className="App">
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={
              !token ? <Login /> : (
                user?.user_type === 'customer' ? (
                  // Check if customer has delivery details (new structure: must have first_name field)
                  user?.delivery_details && 
                  typeof user.delivery_details === 'object' && 
                  Object.keys(user.delivery_details).length > 0 &&
                  user.delivery_details.first_name ? 
                    <Navigate to="/" /> : 
                    <Navigate to="/customer-delivery-setup" />
                ) : <Navigate to="/dashboard" />
              )
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={token && user?.user_type !== 'customer' ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/customer-delivery-setup" element={token && user?.user_type === 'customer' ? <CustomerDeliverySetup /> : <Navigate to="/login" />} />
            <Route path="/customer-dashboard" element={token && user?.user_type === 'customer' ? <CustomerDashboard /> : <Navigate to="/login" />} />
            <Route path="/pos" element={token ? <POS /> : <Navigate to="/login" />} />
            <Route path="/inventory" element={token ? <Inventory /> : <Navigate to="/login" />} />
            <Route path="/quotations" element={token ? <Quotations /> : <Navigate to="/login" />} />
            <Route path="/invoices" element={token ? <Invoices /> : <Navigate to="/login" />} />
            <Route path="/credit-invoices" element={token ? <CreditInvoices /> : <Navigate to="/login" />} />
            <Route path="/customer-invoices" element={token && user?.user_type === 'customer' ? <CustomerInvoices /> : <Navigate to="/login" />} />
            <Route path="/users" element={token ? <UserManagement /> : <Navigate to="/login" />} />
            <Route path="/customers" element={token ? <Customers /> : <Navigate to="/login" />} />
            <Route path="/website-cms" element={token ? <WebsiteCMS /> : <Navigate to="/login" />} />
            <Route path="/settings" element={token ? <Settings /> : <Navigate to="/login" />} />
            <Route path="/invoice-quotation-settings" element={token ? <InvoiceQuotationSettings /> : <Navigate to="/login" />} />
            <Route path="/currency-settings" element={token ? <CurrencySettings /> : <Navigate to="/login" />} />
            <Route path="/accounting" element={token ? <AccountingDashboard /> : <Navigate to="/login" />} />
            <Route path="/expenses" element={token ? <Expenses /> : <Navigate to="/login" />} />
            <Route path="/expenses/settled" element={token ? <SettledExpenses /> : <Navigate to="/login" />} />
            <Route path="/purchase-orders" element={token ? <PurchaseOrders /> : <Navigate to="/login" />} />
            <Route path="/suppliers" element={token ? <Suppliers /> : <Navigate to="/login" />} />
            <Route path="/budget" element={token ? <Budget /> : <Navigate to="/login" />} />
            <Route path="/tax" element={token ? <Tax /> : <Navigate to="/login" />} />
            <Route path="/petty-cash" element={token ? <PettyCash /> : <Navigate to="/login" />} />
            <Route path="/loans" element={token ? <Loans /> : <Navigate to="/login" />} />
            <Route path="/transfers" element={token ? <Transfers /> : <Navigate to="/login" />} />
            <Route path="/profile" element={token ? <Profile /> : <Navigate to="/login" />} />
            <Route path="/reports" element={token ? <SalesReports /> : <Navigate to="/login" />} />
            <Route path="/sales-reports" element={token ? <SalesReports /> : <Navigate to="/login" />} />
            <Route path="/sales-history" element={token ? <SalesHistory /> : <Navigate to="/login" />} />
            <Route path="/costing" element={token ? <Costing /> : <Navigate to="/login" />} />
            <Route path="/orders" element={token ? <Orders /> : <Navigate to="/login" />} />
            <Route path="/" element={<PublicWebsite />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/review/write" element={<WriteReview />} />
            <Route path="*" element={<Navigate to={token ? "/dashboard" : "/"} />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </AuthContext.Provider>
    </HelmetProvider>
  );
}

export default App;